import { useFlags } from "launchdarkly-react-client-sdk";
import { useEffect, useMemo } from "react";

import { useAppSelector } from "app/hooks";
import { SIDE_MENU_REDESIGN_FEATURE } from "constants/feature-flags";
import {
  PRE_AUTH_USER_ID,
  selectCurrentPractice,
  selectCurrentUser
} from "features/auth/auth-slice";
import { useGetSideMenuChoiceQuery } from "features/scheduling-v3/data/api/scheduling-settings.api";
import {
  readSchedulingSettingsCache,
  writeSchedulingSettingsCache
} from "features/scheduling-v3/data/settings-cache";

import {
  SIDE_MENU_DOCUMENT_VERSION,
  mirrorableSideMenuDocument,
  readUseClassic
} from "./side-menu-document";

export interface SideMenuChoice {
  /** The flag is on for this person and the real user has loaded: the switch exists. */
  offered: boolean;
  /** The redesigned menu renders. Never true unless offered: the flag is the brake. */
  useNew: boolean;
  /** `pending` only while an offered person's choice is being read with nothing mirrored. */
  status: "pending" | "settled";
  /** The stored document, for the writers to build on; `null` when there is none. */
  document: unknown;
  /**
   * `document` is the server's own record. It is not while answering from the session
   * mirror or after a failed read: a write must never build on those, or it would erase
   * the reasons the person gave for switching back.
   */
  fromServer: boolean;
}

/**
 * A session mirror of the choice, so a warm load paints the chosen menu without a round
 * trip (the same arrangement as the scheduler choice's D14 mirror). Per person, under its
 * own key, outside ENG-2047's sweep prefix.
 */
const mirrorKey = (userId: number) => `suno.side-menu-choice.c1.${userId}`;

/**
 * ENG-2535: the one place that combines the `sidebar-redesign` flag with the person's own
 * choice. The layout, the top bar and both menus ask this, never the flag.
 *
 * Flag off, or nobody loaded yet: the classic rail, exactly as before the redesign. Flag on:
 * the new menu, unless the person switched back. A read that fails or is still in flight
 * (with nothing mirrored) also means the new menu, so the pilot's default never waits on
 * the network.
 */
export function useSideMenuChoice(): SideMenuChoice {
  const flags = useFlags();
  const user = useAppSelector(selectCurrentUser);
  const practice = useAppSelector(selectCurrentPractice);

  const userId = user?.id;
  const practiceId = practice?.id;
  const userLoaded =
    userId != null && userId !== PRE_AUTH_USER_ID && practiceId != null;
  const offered = flags[SIDE_MENU_REDESIGN_FEATURE] === true && userLoaded;

  // `currentData`, not `data`: after a practice switch `data` still holds the previous
  // answer until the new read lands, and `skip` never clears it.
  const { currentData, isError } = useGetSideMenuChoiceQuery(
    { userId: userId ?? 0, practiceId: practiceId ?? 0 },
    { skip: !offered }
  );

  const key = offered && userId != null ? mirrorKey(userId) : null;
  const mirrored = useMemo(
    () => (key ? (readSchedulingSettingsCache(key)?.document ?? null) : null),
    [key]
  );
  useEffect(() => {
    if (!key || currentData === undefined || isError) return;
    writeSchedulingSettingsCache(key, {
      c: 1,
      v: SIDE_MENU_DOCUMENT_VERSION,
      document: mirrorableSideMenuDocument(currentData),
      updated_at: new Date().toISOString()
    });
  }, [key, currentData, isError]);

  return resolveChoice({ offered, currentData, isError, mirrored });
}

/** The decision itself, kept out of the hook so it reads as a table and tests as a function. */
function resolveChoice({
  offered,
  currentData,
  isError,
  mirrored
}: {
  offered: boolean;
  currentData: unknown;
  isError: boolean;
  mirrored: unknown;
}): SideMenuChoice {
  if (!offered) {
    return {
      offered: false,
      useNew: false,
      status: "settled",
      document: null,
      fromServer: false
    };
  }
  const settled = currentData !== undefined || isError;
  // The read is still in flight: answer from what the server said earlier this session.
  const fromMirror = !settled && mirrored !== null;
  const document = fromMirror
    ? mirrored
    : isError
      ? null
      : (currentData ?? null);
  return {
    offered,
    useNew: !readUseClassic(document),
    status: settled || fromMirror ? "settled" : "pending",
    document,
    fromServer: settled && !isError
  };
}
